import { Component, OnInit, ViewChild, ElementRef, Input, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import * as d3 from 'd3';
import { StoredSettingsService } from '../../../services/stored-settings.service';
import { chartSettings } from '../../../../models/chartSettings';
import { SettingTypes } from '../../../../shared/enums/settingTypes';
import { StoredSetting } from '../../../../models/storedSetting';
@Component({
    selector: 'app-multi-graph',
    templateUrl: './multi-graph.component.html',
    styleUrls: ['./multi-graph.component.css'],
    standalone: false
})
export class MultiGraphComponent implements OnInit {

  @Input() xData: number[];
  @Input() yData: {data: number[], std: number[], lineColor: string, name: string}[];
  @Input() displayingIndices: number[];
  @Input() wideGraph: boolean = false;
  @Input() frequencyDividers: {frequency: any, iteration: any}[];
  @Input() scale: number[] = null;
  @Output('point') point: EventEmitter<{index: number, types: number[]}> = new EventEmitter<{index: number, types: number[]}>();
  @ViewChild('graph', { static: true }) graph: ElementRef;
  @ViewChild('graphContainer', { static: true }) container: ElementRef;

  selectedTypes = []
  plotTypes = []
  showStd = false
  showBlockLabels = false
  showGridLines = false;
  // just for storing std
  settingsKey = "charts";
  settingsId = "std"

  private width: number;
  private height: number;
  private margin = {top: 0.1, right: 0.1, bottom: 0.1, left: 0.12};
  private svg;
  private xScale;
  private yScale;
  private legendLabelPosition = 0;
  private legendPosition: 'top-left' | 'bottom-left' | 'bottom-right' | 'top-right' = 'bottom-left';
  private lines =[]
  private selectedNode: number;

  constructor(
    private storedSettingsService: StoredSettingsService,
  ) { }

  ngOnInit() {
    let settings = this.storedSettingsService.getSettings<chartSettings>(this.settingsKey, this.settingsId, SettingTypes.ChartSettings);
    if (settings != null) {
      this.showStd = settings.showStd;
    }
    this.selectedTypes = this.displayingIndices;
    this.getPlotTypes();
  }

  ngAfterViewInit() {
    this.setUpCanvas();
    this.drawSelectedLines()
  }

  getPlotTypes() {
    this.plotTypes = this.yData.map((d, i) => {
      return {name: d.name, index: i};
    });
  }

  changeType(plotType, event) {
    event.stopPropagation()
    let index = plotType.index;
    if (this.selectedTypes.includes(index)) {
      let indexPostion = this.selectedTypes.indexOf(index);
      if (indexPostion >= 0) {
        this.selectedTypes.splice(indexPostion, 1);
      }
    }else {
      this.selectedTypes.push(index);
    }
    this.drawSelectedLines();
  }

  turnOnOffStd() {
    this.showStd = !this.showStd;
    this.drawSelectedLines();
    let settings:chartSettings = {id: this.settingsId, showStd:this.showStd, projectId:null, iteration:null};
    this.storedSettingsService.setSettings(this.settingsKey, settings, SettingTypes.ChartSettings);
  }

  toggleBlockLabels(){
    this.showBlockLabels = !this.showBlockLabels
    if (this.showBlockLabels){
      // Frequency divider lines
      let filteredFrequencyDividers = []
      if (this.xData.length == 1){
        filteredFrequencyDividers = [this.frequencyDividers[0]];
      }else{
        filteredFrequencyDividers = this.frequencyDividers;
      }
      const g = this.svg.append("g").attr('class', 'block-labels-container').attr("clip-path", "url(#rect_clip)");
      g.selectAll(".vertical-line")
        .data(filteredFrequencyDividers)
        .enter()
        .append("line")
        .attr("class", "vertical-line")
        .attr("x1", d => this.xScale(d.iteration))
        .attr("y1", 0) // Start at the top of the inner height
        .attr("x2", d => this.xScale(d.iteration))
        .attr("y2", this.height) // End at the bottom of the inner height
        .style("stroke", "#0000FF") // Blue color
        .style("stroke-width", 1)
        .style("stroke-opacity", 0.5) 
        .style("stroke-dasharray", "3,3"); // Optional: add dashed style
      g.selectAll(".vertical-line-label")
        .data(filteredFrequencyDividers)
        .enter()
        .append("text")
        .attr("class", "vertical-line-label")
        .attr("x", d => this.xScale((d.iteration - (d.width/2)+ 0.5)))
        .attr("y", 10) // Adjust vertical position above the line
        .attr("text-anchor", "middle") // Center the text horizontally
        .style("font-size", "10px")
        .text(d => `${d.frequency} Hz`); // Customize the label text
      g.selectAll(".vertical-line-functional-label")
        .data(filteredFrequencyDividers)
        .enter()
        .append("text")
        .attr("class", "vertical-line-label")
        .attr("x", d => this.xScale((d.iteration - (d.width/2))+ 0.5))
        .attr("y", 20) // Adjust vertical position above the line
        .attr("text-anchor", "middle") // Center the text horizontally
        .style("font-size", "10px")
        .text(d => `${d.functional}`); // Customize the label text
    }else{
      if (this.svg) {
        this.svg.selectAll('.block-labels-container').remove();
      }
    }
  }

  toggleGridLines(){
    this.showGridLines = !this.showGridLines;
    if (this.showGridLines){
      
      const gridContainer = this.svg.insert("g", ":first-child")
        .attr('class', 'grid-lines-container')
        .attr("clip-path", "url(#rect_clip)");
      
      // X-axis grid lines (vertical lines)
      const xGridAxis = d3.axisBottom(this.xScale)
      .tickValues(this.xScale.ticks().filter(tick => Number.isInteger(tick)))
        .tickSize(-this.height)
        .tickFormat(null); // Remove tick labels
      
      gridContainer.append("g")
        .attr("class", "x-grid")
        .attr("transform", `translate(0,${this.height})`)
        .call(xGridAxis)
        .selectAll("line")
        .style("opacity", 0.3)
        .style("stroke-dasharray", "3,3");
      
      // Y-axis grid lines (horizontal lines)
      const yGridAxis = d3.axisLeft(this.yScale)
        .tickSize(-this.width)
        .tickFormat(null); // Remove tick labels
      
      gridContainer.append("g")
        .attr("class", "y-grid")
        .call(yGridAxis)
        .selectAll("line")
        .style("opacity", 0.3)
        .style("stroke-dasharray", "3,3");
      
      // Remove the domain lines (the main axis lines)
      gridContainer.selectAll(".domain").remove();
    }else{
      if (this.svg) {
        this.svg.selectAll('.grid-lines-container').remove();
      }
    }
  }

  private drawSelectedLines() {
    this.lines.map(l => l.remove());
    this.lines = [];
    this.legendLabelPosition = 0;
    
    // Collect all lines data for overlap detection
    const allLineData = [];
    for (let index of this.selectedTypes) {
      let yData = this.yData[index].data;
      let data: number[][] = this.xData.map((e, i) => [e, yData[i]]).filter(d => d[1] !== null);
      data.sort((a, b) => a[0]-b[0]);
      allLineData.push(data);
    }
    
    // Determine best legend position avoiding overlaps
    this.legendPosition = this.determineLegendPosition(allLineData);
    
    
    for (let index of this.selectedTypes) {
      let lineColor = this.yData[index].lineColor;
      let yData = this.yData[index].data;
      let std = this.yData[index].std;
      let name = this.yData[index].name;
      this.drawLine(this.xData, yData, std, lineColor, name, ++this.legendLabelPosition);
    }
    this.point.emit({index: this.selectedNode, types: this.selectedTypes});
  }

  private setUpCanvas() {
    let xData = this.xData;
    let yData = this.yData.map(d => d.data).reduce((a, c) => a.concat(c), []).filter(d => d!==null);
    let std = this.yData.map(d => d.std).reduce((a, c) => a.concat(c), []).filter(d => d!==null);
    this.width = this.container.nativeElement.offsetWidth*0.875;
    if (this.wideGraph) {
      this.height = this.width*0.375;
      this.margin.left = 0.07;
    } else {
      this.height = this.width*0.75;
    }

    let xRange = Math.max(...xData)-Math.min(...xData);
    let minX = Math.min(...xData)-0.05*xRange;
    let maxX = Math.max(...xData)+0.05*xRange;

    let minY = Math.min(...yData)
    let maxY = Math.max(...yData)
    if (!!std) {
      std = std.filter(d => d !== null);
      minY = Math.min(...this.arrayOperation(yData, std, (x,y) => x-y/2));
      maxY = Math.max(...this.arrayOperation(yData, std, (x,y) => x+y/2));
    }
    if (minY == maxY) {
      minY *= 0.9;
      maxY *= 1.1;
    }
    if (this.scale[0] != null) {
      minY = this.scale[0];
    }
    if (this.scale[1] != null && this.scale[1] > minY) {
      maxY = this.scale[1];
    }
    let yRange = maxY - minY;
    minY = minY-0.05*yRange;
    maxY =  maxY+0.05*yRange;

    let xScale = d3.scaleLinear()
                   .domain([minX, maxX])
                   .range([0, this.width]);
    let yScale = d3.scaleLinear()
                   .domain([minY, maxY])
                   .range([this.height, 0]);

    let svg = d3.select(this.graph.nativeElement)
                .attr("width", this.width*(1+this.margin.left+this.margin.right))
                .attr("height", this.height*(1+this.margin.top+this.margin.bottom))
                .append("g")
                .attr("transform", 'translate('+this.width*this.margin.left+', '+this.height*this.margin.top+')');
    
    // Create an x-axis with only integer ticks
    const xAxis = d3.axisBottom(xScale)
      .tickValues(xScale.ticks().filter(tick => Number.isInteger(tick)))
      .tickFormat(d3.format('d'));
    svg.append("g").attr("transform", 'translate(0,'+this.height+')').call(xAxis);

    svg.append("g").call(d3.axisLeft(yScale));
    this.svg = svg;
    this.xScale = xScale;
    this.yScale = yScale;
    this.toggleBlockLabels()
    this.toggleGridLines()
  }

  private drawLine(xData: number[], yData: number[], std: number[],
    line_color: string, name: string, index: number) {
    let xScale = this.xScale;
    let yScale= this.yScale

    let svg = this.svg.append("g").attr("id", name);
    this.lines.push(svg);
    let data: number[][] = xData.map((e, i) => [e, yData[i]]).filter(d => d[1] !== null);
    std = std.filter(d => d !== null);
    data.sort((a, b) => a[0]-b[0])
    std = std.slice(0, data.length)
    // Add path
    let line = d3.line().x(function(d: number[]) {return xScale(d[0]);})
                        .y(function(d: number[]) {return yScale(d[1]);});

    svg.append("g").selectAll("path").data([data]).enter()
       .append("path")
       .attr("class", "fline")
       .style("stroke", line_color)
       .attr("d",line).attr("clip-path", "url(#rect_clip)")
       .attr("data-legend", () => name)
       .attr("stroke-width", 1.5).style("fill", "none");
    // Add nodes
    svg.append("g").selectAll("circle").data(data).enter().append("circle")
       .attr("cx", function(d: number[]) {return xScale(d[0])})
       .attr("cy", function(d: number[]) {;return yScale(d[1])}).attr("r", 5)
       .style("fill", line_color)
       .on("mouseover", function() {d3.select(this).attr("r", 8).style("opacity", 0.7)})
       .on("mouseout", function() {d3.select(this).attr("r", 5).style("opacity", 1)})
       .on("click",(event, d) => {
         this.selectedNode = xData.indexOf(d[0])
         this.point.emit({index: this.selectedNode, types: this.selectedTypes})
        })
       .attr("clip-path", "url(#rect_clip");
    svg.append("clipPath").attr("id", "rect_clip").append("rect").attr("x", 0).attr("y", 0).attr("height", this.height).attr("width", this.width);

    svg.append("text").attr("transform", 'translate('+ (this.width/2) +','+(this.height+40)+')')
       .text("Iteration").style("font-weight", "bold")

    const legendPos = this.calculateLegendPosition(index);
    let legend = svg.append("g")
     .attr("transform",`translate(${legendPos.x},${legendPos.y})`)

  legend.append("circle")
        .attr("r", 5)
        .style("fill", line_color)

  legend.append("text")
        .attr("x", 20)
        .style("font-size", "12px")
        .attr("alignment-baseline","middle")
        .text(name)

    if (!this.showStd) return;
    //Add error lines
    svg.append("g").selectAll("line").data(std).enter().append("line").attr("class", "errorline")
        .attr("stroke", "#EF5656").attr("stroke-dasharray", "2,2")
       .attr("x1", function(_d: number, i: number) { return xScale(data[i][0]) })
       .attr("x2", function(_d: number, i: number) { return xScale(data[i][0]) })
       .attr("y1", function(d: number, i: number) { return yScale(data[i][1]+d/2) })
       .attr("y2", function(d: number, i: number) { return yScale(data[i][1]-d/2) });
    svg.append("g").selectAll("line").data(std).enter().append("line").attr("class", "errorcap")
        .attr("stroke", "#EF5656").attr("stroke-width","2px")
       .attr("x1", function(_d: number, i: number) { return xScale(data[i][0])-5 })
       .attr("x2", function(_d: number, i: number) { return xScale(data[i][0])+5 })
       .attr("y1", function(d: number, i: number) { return yScale(data[i][1]+d/2) })
       .attr("y2", function(d: number, i: number) { return yScale(data[i][1]+d/2) });
    svg.append("g").selectAll("line").data(std).enter().append("line").attr("class", "errorcap")
        .attr("stroke", "#EF5656").attr("stroke-width","2px")
       .attr("x1", function(_d: number, i: number) { return xScale(data[i][0])-5 })
       .attr("x2", function(_d: number, i: number) { return xScale(data[i][0])+5 })
       .attr("y1", function(d: number, i: number) { return yScale(data[i][1]-d/2) })
       .attr("y2", function(d: number, i: number) { return yScale(data[i][1]-d/2) });
  }

  private arrayOperation(s: number[], t: number[], operation: (x: number, y: number) => number) {
    if (s.length != t.length) return;
    return s.map((e, i) => operation(e, t[i]));
  }

  private determineLegendPosition(allLineData: number[][][]): 'top-left' | 'bottom-left' | 'bottom-right' | 'top-right' {
    const legendWidth = 150;
    const legendHeight = this.selectedTypes.length * 15 + 20;
    const leftX = 25;
    
    // Check bottom-left position
    const bottomLegendTop = this.height - legendHeight - 10;
    const bottomLegendBottom = this.height - 10;
    
    let bottomLeftOverlap = false;
    let topLeftOverlap = false;
    let bottomRightOverlap = false;
    // Check all line data for overlaps
    for (let lineData of allLineData) {
      for (let point of lineData) {
        const x = this.xScale(point[0]);
        const y = this.yScale(point[1]);
        
        // Check bottom-left overlap
        if (x >= leftX && x <= leftX + legendWidth && 
            y >= bottomLegendTop && y <= bottomLegendBottom) {
          bottomLeftOverlap = true;
        }
        
        // Check top-left overlap
        if (x >= leftX && x <= leftX + legendWidth && 
            y >= 10 && y <= 10 + legendHeight) {
          topLeftOverlap = true;
        }

        // Check bottom-right overlap
        if (x >= this.width - legendWidth && x <= this.width - 25 && 
            y >= bottomLegendTop && y <= bottomLegendBottom) {
          bottomRightOverlap = true;
        }
      }
    }
    
    // Determine best position
    if (!bottomLeftOverlap) {
      return 'bottom-left';
    } else if (!topLeftOverlap) {
      return 'top-left';
    } else if (!bottomRightOverlap) {
      return 'bottom-right';
    } else {
      return 'top-right';
    }
  }
  
  private calculateLegendPosition(index: number): { x: number, y: number } {
    const legendWidth = 150;
    
    switch (this.legendPosition) {
      case 'top-left':
        return {
          x: 25,
          y: 40 + (index - 1) * 15
        };
      case 'bottom-right':
        return {
          x: this.width - legendWidth,
          y: this.height - (this.selectedTypes.length - index + 1) * 15 - 10
        };
      case 'bottom-left':
        return {
          x: 25,
          y: this.height - (this.selectedTypes.length - index + 1) * 15 - 10
        };
      case 'top-right':
        return {
          x: this.width - legendWidth,
          y: 40 + (index - 1) * 15
        };
      default:
        return {
          x: this.width - legendWidth,
          y: 40 + (index - 1) * 15
        };
    }
  }


}